import { useMemo, useState } from 'react';
import { haptics } from '../../telegram';
import type { RatingHistoryPoint, RatingLevel } from '../../api/types';

const W = 296;
const H = 148;
const PAD_L = 10;
const PAD_R = 52; // место под подписи порогов справа
const PAD_T = 14;
const PAD_B = 20;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * График динамики личного рейтинга (RB1): последние 20 игр, пороги уровней
 * пунктиром, золотое кольцо на пике, тап по точке — ореол + тултип. Ручной
 * SVG (чарт-библиотек в проекте нет, прецедент геометрии — Medallion).
 */
export function RatingChart({
  history,
  levels,
  peakValue,
}: {
  history: RatingHistoryPoint[];
  levels: RatingLevel[];
  peakValue: number | null;
}) {
  const [sel, setSel] = useState<number | null>(null); // null = последняя точка

  const geo = useMemo(() => {
    if (history.length < 2) return null;
    const values = history.map((h) => h.value);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (max - min < 40) {
      const mid = (max + min) / 2;
      min = mid - 20;
      max = mid + 20;
    }
    const span = max - min;
    min -= span * 0.12;
    max += span * 0.12;
    const x = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / (history.length - 1);
    const y = (v: number) => PAD_T + ((max - v) * (H - PAD_T - PAD_B)) / (max - min);
    const thresholds = levels.filter((l) => l.threshold > min && l.threshold < max && l.n > 1);
    return { min, max, x, y, thresholds };
  }, [history, levels]);

  if (!geo) return null;
  const { x, y, thresholds } = geo;

  const selIdx = sel ?? history.length - 1;
  const p = history[selIdx];
  const peakIdx =
    peakValue === null ? -1 : history.findIndex((h) => h.value === peakValue && h.kind === 'game');
  const path = history.map((h, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(h.value).toFixed(1)}`).join(' ');
  const tipLeftPct = (x(selIdx) / W) * 100;

  return (
    <div className="rating-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="rating-chart-svg">
        {thresholds.map((l) => (
          <g key={l.n}>
            <line
              x1={PAD_L}
              x2={W - PAD_R + 6}
              y1={y(l.threshold)}
              y2={y(l.threshold)}
              className="rc-threshold"
              strokeDasharray="3 4"
            />
            <text x={W - PAD_R + 10} y={y(l.threshold) + 2.5} className="rc-threshold-label">
              {l.threshold} · УР.{l.n}
            </text>
          </g>
        ))}
        <path d={path} className="rc-line" fill="none" />
        {history.map((h, i) => (
          <g key={`${h.tournamentId}-${h.kind}-${i}`}>
            {i === selIdx && <circle cx={x(i)} cy={y(h.value)} r={9} className="rc-halo" />}
            {i === peakIdx && <circle cx={x(i)} cy={y(h.value)} r={7} className="rc-peak-ring" />}
            <circle cx={x(i)} cy={y(h.value)} r={4.5} className={`rc-dot ${h.kind === 'decay' ? 'rc-dot--decay' : ''}`} />
            {/* широкая невидимая зона тапа */}
            <rect
              x={x(i) - 8}
              y={0}
              width={16}
              height={H}
              fill="transparent"
              onClick={() => {
                haptics.tap();
                setSel(i);
              }}
            />
          </g>
        ))}
        {peakIdx >= 0 && (
          <text x={x(peakIdx)} y={y(history[peakIdx].value) - 11} textAnchor="middle" className="rc-peak-label">
            ПИК {history[peakIdx].value}
          </text>
        )}
        <text x={PAD_L} y={H - 6} className="rc-axis">
          {history.length} ИГР НАЗАД
        </text>
        <text x={W - PAD_R + 6} y={H - 6} textAnchor="end" className="rc-axis">
          СЕЙЧАС
        </text>
      </svg>
      <div
        className="rc-tooltip"
        style={{ left: `clamp(12%, ${tipLeftPct}%, 88%)` }}
        key={`${p.tournamentId}-${p.kind}`}
      >
        {p.kind === 'decay' ? (
          <>
            <div className="t">Неактивность {fmtDate(p.date)}</div>
            <div className="d">{p.delta} за пропуск</div>
          </>
        ) : (
          <>
            <div className="t">Турнир {fmtDate(p.date)}</div>
            <div className="d">
              {p.place !== null && p.fieldSize !== null ? `${p.place} место из ${p.fieldSize} · ` : ''}
              <span className={p.delta >= 0 ? 'up' : 'down'}>
                {p.delta >= 0 ? `+${p.delta}` : p.delta}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
